import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/ui/design-system/components/ui/dialog';
import {
    Alert,
    AlertDescription,
} from '@/ui/design-system/components/ui/alert';
import { Button } from '@/ui/design-system/components/ui/button';
import { Input } from '@/ui/design-system/components/ui/input';
import { Label } from '@/ui/design-system/components/ui/label';
import { useGenerateApiKey } from 'utils/hooks/apiKeys';

function CreateKeyDialog({ open, onOpenChange, onKeyCreated }) {
    const [name, setName] = useState('');
    const generateKey = useGenerateApiKey();

    const handleCreate = async () => {
        try {
            const result = await generateKey.mutateAsync(name);
            setName('');
            generateKey.reset();
            onOpenChange(false);
            onKeyCreated(result.secret_key);
        } catch {
            // surfaced via generateKey.error below
        }
    };

    const handleClose = (value) => {
        if (!value) {
            setName('');
            generateKey.reset();
        }
        onOpenChange(value);
    };

    const errorMessage = generateKey.isError
        ? generateKey.error?.message ||
          'Something went wrong creating your API key.'
        : null;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Create API Key</DialogTitle>
                    <DialogDescription>
                        Give your API key a descriptive name to help you
                        identify it later.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                    <Label htmlFor="key-name">Key Name</Label>
                    <Input
                        id="key-name"
                        placeholder="e.g., Production_Server_01"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && name.trim()) {
                                handleCreate();
                            }
                        }}
                    />
                </div>
                {errorMessage && (
                    <Alert variant="destructive">
                        <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>
                )}
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => handleClose(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={!name.trim() || generateKey.isLoading}
                        onClick={handleCreate}
                    >
                        {generateKey.isLoading ? 'Creating...' : 'Create'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CreateKeyDialog;
